import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { Button, buttonVariants } from 'components/ui/button';
import BinahMonitor from './BinahMonitor';
import MedsiStartScanPage from './medsi/MedsiStartScanPage';
import { useGetReScanConfiguration } from 'services/face-scan/hooks';
import { PATH_ROUTES } from 'routes/path';
import useAuthStore from 'store/auth';
import Loader from 'components/Loader';
import ScanPreference from 'components/scan-preference/ScanPreference';
import { isMedsiFaceScanModel } from 'lib/face-scan-model';

import SitComfortably from 'assets/images/face-scan/sit-comfortably.png';
import EvenlyIlluminated from 'assets/images/face-scan/evenly-illuminated.png';
import AvoidMoving from 'assets/images/face-scan/avoid-moving.png';

export default function StartScanPage() {
  const { t } = useTranslation();

  const { pathname } = useLocation();
  const hasTrailingSlash = pathname.endsWith('/');

  const [searchParams] = useSearchParams();
  const show_scan = searchParams.get('showScan');

  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const [showInstruction, setShowInstruction] = useState<boolean>(true);
  const [isWebSelected, setIsWebSelected] = useState<boolean>(false);

  const { data, isFetching } = useGetReScanConfiguration({
    profileId,
  });

  useEffect(() => {
    show_scan && setShowInstruction(false);
  }, [show_scan]);

  if (hasTrailingSlash)
    return <Navigate to={PATH_ROUTES.START_FACE_SCAN} replace />;

  if (isFetching) return <Loader />;

  if (!data?.rescan_flag) {
    return (
      <div className='space-y-6 max-w-sm mx-auto h-[calc(100vh-8rem)] flex flex-col justify-center items-center'>
        <div>
          <h1 className='text-xl font-semibold text-center sm:text-2xl'>
            {data?.error}
          </h1>
          <p className='text-center'>{data?.error_msg}</p>
        </div>
        <Link
          to={PATH_ROUTES.FACE_SCAN}
          className={buttonVariants({ className: 'w-1/2' })}
        >
          {t('ok')}
        </Link>
      </div>
    );
  }

  if (isMedsiFaceScanModel()) {
    return <MedsiStartScanPage />;
  }

  return (
    <>
      {!isWebSelected ? (
        <ScanPreference onWebSelect={() => setIsWebSelected(true)} />
      ) : showInstruction ? (
        <ScanInstruction onClose={() => setShowInstruction(false)} />
      ) : (
        <BinahMonitor />
      )}
    </>
  );
}

function ScanInstruction({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();

  const INSTRUCTIONS = [
    { text: t('sit_comfortably'), img: SitComfortably },
    { text: t('face_is_evenly_illuminated'), img: EvenlyIlluminated },
    { text: t('avoid_moving_throughout_measurement'), img: AvoidMoving },
  ];

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  return (
    <div className='flex items-center justify-center h-[calc(100vh-12rem)] lg:h-full max-w-sm mx-auto'>
      <div className='px-6 space-y-4 sm:px-0'>
        <div className='min-h-80'>
          <div className='mx-auto size-60'>
            <img
              src={INSTRUCTIONS[currentIndex].img}
              alt='image'
              className='object-contain w-full h-full'
            />
          </div>
          <p className='font-medium leading-tight text-center'>
            {INSTRUCTIONS[currentIndex].text}
          </p>
        </div>
        <div className='flex flex-col justify-center gap-4 sm:flex-row'>
          <Button
            variant='outline'
            className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
            disabled={currentIndex === 0}
            onClick={() => {
              setCurrentIndex((prev) => prev - 1);
            }}
          >
            {t('previous')}
          </Button>
          <Button
            variant='outline'
            size='sm'
            className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
            onClick={() => {
              if (currentIndex === INSTRUCTIONS.length - 1) onClose();
              setCurrentIndex((prev) => prev + 1);
            }}
          >
            {t('next')}
          </Button>
          <Button
            variant='outline'
            className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
            onClick={onClose}
          >
            {t('skip')}
          </Button>
        </div>
      </div>
    </div>
  );
}
